import React, {useEffect, useState} from "react";
import {Box, ButtonBase, Stack, Typography} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import {ComplianceIndicatorsDto, ComplianceIndicatorsRestControllerApiFactory, Indicator, IndicatorToneEnum} from "arteco-api-client-ts";
import dayjs from "dayjs";
import {useNavigate} from "react-router-dom";
import {useApiManager} from "../../../components/utils/api";
import {EmptyState, Page, PageHeader, Panel, SkeletonRows, SoftChip, StatGrid, StatTile, Tone} from "../../../components/kit";
import {COMPLIANCE, useComplianceAccess} from "./access";

const TONE: Record<string, Tone> = {
	[IndicatorToneEnum.GOOD]: "success",
	[IndicatorToneEnum.WARNING]: "warning",
	[IndicatorToneEnum.BAD]: "error",
	[IndicatorToneEnum.EMPTY]: "neutral",
	[IndicatorToneEnum.NO_SOURCE]: "neutral",
};

const TONE_LABEL: Record<string, string> = {
	[IndicatorToneEnum.GOOD]: "En orden",
	[IndicatorToneEnum.WARNING]: "Atención",
	[IndicatorToneEnum.BAD]: "Mal",
	[IndicatorToneEnum.EMPTY]: "Sin registros",
	[IndicatorToneEnum.NO_SOURCE]: "Sin fuente",
};

/** Dónde se ve el registro de cada indicador; los de RR. HH., solo para quien entra en RR. HH. */
const SOURCE_LINK: Record<string, { to: string, hr?: boolean }> = {
	I2: {to: `${COMPLIANCE}/support`},
	I5: {to: `${COMPLIANCE}/finding`},
	I6: {to: `${COMPLIANCE}/finding`},
	I9: {to: `${COMPLIANCE}/access-review`},
	I10: {to: "/app/admin/employee/training", hr: true},
	I11: {to: `${COMPLIANCE}/document`},
	I12: {to: "/app/admin/employee/interview", hr: true},
	I13: {to: `${COMPLIANCE}/document`},
	I14: {to: `${COMPLIANCE}/required-documents`},
};

/**
 * Portada de compliance (#13899): los indicadores I1–I13 del sistema de gestión
 * (ref-indicadores.md). Los que tienen registro en la Extranet salen calculados; el resto, «sin
 * fuente» y sin cifra.
 */
export const ComplianceDashboardPage = () => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(ComplianceIndicatorsRestControllerApiFactory);
	const [data, setData] = useState<ComplianceIndicatorsDto>();
	const [error, setError] = useState(false);

	useEffect(() => {
		apiManager.execute(ctrl.indicatorsCompliance()).then(setData).catch(() => setError(true));
	}, []);

	const list = data?.indicators || [];
	const withSource = list.filter(i => i.source);
	const count = (tone: IndicatorToneEnum) => list.filter(i => i.tone === tone).length;

	return (
		<Page>
			<PageHeader title="Compliance"
						subtitle={data ? `Indicadores del sistema de gestión a ${dayjs(data.date).format("DD/MM/YYYY")}, calculados de los registros de la Extranet`
							: "Indicadores del sistema de gestión"}/>
			<StatGrid>
				<StatTile loading={!data && !error} icon={<InsightsRoundedIcon/>} tone="info" label="Con fuente en la Extranet"
						  value={`${withSource.length} de ${list.length}`}/>
				<StatTile loading={!data && !error} icon={<CheckCircleOutlineRoundedIcon/>} tone="success" label="En orden"
						  value={count(IndicatorToneEnum.GOOD)}/>
				<StatTile loading={!data && !error} icon={<WarningAmberRoundedIcon/>} tone="warning" label="Piden atención"
						  value={count(IndicatorToneEnum.WARNING) + count(IndicatorToneEnum.BAD)}/>
				<StatTile loading={!data && !error} icon={<HelpOutlineRoundedIcon/>} tone="neutral" label="Sin fuente"
						  value={count(IndicatorToneEnum.NO_SOURCE)}/>
			</StatGrid>

			<Panel title="Indicadores" subtitle="Qué se mide, quién responde de ello y cada cuánto se mira.">
				{error ? (
					<EmptyState icon={<WarningAmberRoundedIcon/>} title="No se han podido calcular los indicadores"
								description="Vuelve a cargar la página; si sigue fallando, avisa con el botón de abajo a la derecha."/>
				) : !data ? <SkeletonRows rows={6} height={120}/> : (
					<Box sx={{
						display: "grid", gap: 1.5,
						gridTemplateColumns: {xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))"},
					}}>
						{list.map(i => <IndicatorCard key={i.code} indicator={i}/>)}
					</Box>
				)}
				<Stack direction="row" spacing={1} sx={{mt: 2, color: "text.secondary"}}>
					<InfoOutlinedIcon sx={{fontSize: 16, mt: "2px"}}/>
					<Typography variant="caption">
						Catálogo aprobado por la dirección el 15/10/2026 (ref-indicadores.md); las metas, en los objetivos del sistema.
						Un indicador sin fuente no tiene cifra hasta que exista su registro: no se rellena a mano.
					</Typography>
				</Stack>
			</Panel>
		</Page>
	);
};

const IndicatorCard = ({indicator: i}: { indicator: Indicator }) => {
	const navigate = useNavigate();
	const access = useComplianceAccess();
	const tone = TONE[i.tone || IndicatorToneEnum.NO_SOURCE] || "neutral";
	const link = SOURCE_LINK[i.code || ""];
	const to = link && (!link.hr || access.hr) ? link.to : undefined;

	return (
		<ButtonBase disabled={!to} onClick={() => to && navigate(to)} sx={{
			display: "flex", flexDirection: "column", alignItems: "stretch", textAlign: "left", height: "100%",
			p: 2, borderRadius: 2.5, border: "1px solid", borderColor: "divider", bgcolor: "background.paper",
			opacity: i.source ? 1 : 0.85, transition: "border-color .15s",
			"&:hover": to ? {borderColor: "primary.main"} : undefined,
		}}>
			<Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
				<Typography variant="caption" color="text.secondary" fontWeight={600} noWrap>
					{i.code} · {i.process}
				</Typography>
				<SoftChip tone={tone} label={TONE_LABEL[i.tone || IndicatorToneEnum.NO_SOURCE]}/>
			</Stack>
			<Typography variant="subtitle2" fontWeight={700} sx={{mt: 1}}>{i.name}</Typography>
			{i.source && (
				<Typography variant="h6" fontWeight={700} sx={{mt: 0.5, fontVariantNumeric: "tabular-nums"}}>{i.value}</Typography>
			)}
			<Typography variant="body2" color="text.secondary" sx={{mt: 0.5, flexGrow: 1}}>{i.detail}</Typography>
			<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mt: 1.5}}>
				<Typography variant="caption" color="text.secondary">
					{i.responsible} · {(i.frequency || "").toLowerCase()}
				</Typography>
				{to && <ChevronRightRoundedIcon fontSize="small" sx={{color: "text.disabled"}}/>}
			</Stack>
		</ButtonBase>
	);
};
